"use client";
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const servicesRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if current path is active
  const isActiveRoute = (path) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/home';
    }
    return location.pathname === path;
  };

  // Check if services dropdown should be active
  const isServicesActive = () => {
    return location.pathname === '/consulting' || location.pathname === '/outsourcing';
  };

  const handleNavClick = (itemName, path = null) => {
    if (path) {
      navigate(path);
      // Scroll to top after navigation
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    } else {
      alert(`${itemName} - Coming Soon!`);
    }
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  };

  const handleServicesClick = (serviceName, path) => {
    navigate(path);
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
    setIsServicesOpen(false);
    setIsMenuOpen(false);
  };

  // Handle logo click with proper event handling
  const handleLogoClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('Logo clicked - navigating to home'); // Debug log
    navigate('/');
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };

  const navigationItems = [
    { name: 'Home', href: '/', hasPath: true },
    { name: 'About', href: '/about', hasPath: true },
    { name: 'Products', href: '/product', hasPath: true },
    { name: 'Other Services', href: '#', hasPath: false, isDropdown: true },
    { name: 'Careers', href: '/careers', hasPath: true },
  ];

  const servicesItems = [
    { name: 'Consulting', path: '/consulting' },
    { name: 'Outsourcing', path: '/outsourcing' }
  ];

  // Creative animation variants for logo
  const logoVariants = {
    hidden: {
      opacity: 0,
      scale: 0.5,
      y: -30,
      rotate: -10
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      rotate: 0,
      transition: {
        duration: 1.2,
        ease: "easeOut",
        delay: 0.3,
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    },
    hover: {
      scale: 1.1,
      rotate: [0, -2, 2, 0],
      transition: {
        duration: 0.6,
        ease: "easeInOut",
        rotate: {
          repeat: 1,
          duration: 0.3
        }
      }
    },
    tap: {
      scale: 0.95,
      rotate: 5,
      transition: {
        duration: 0.1
      }
    }
  };

  // Floating animation for continuous attention
  const floatingVariants = {
    animate: {
      y: [0, -8, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  // Pulse/Glow animation that triggers periodically
  const pulseVariants = {
    animate: {
      scale: [1, 1.05, 1],
      filter: [
        "drop-shadow(0 0 0px rgba(59, 130, 246, 0))",
        "drop-shadow(0 0 20px rgba(59, 130, 246, 0.6))",
        "drop-shadow(0 0 0px rgba(59, 130, 246, 0))"
      ],
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatDelay: 5,
        ease: "easeInOut"
      }
    }
  };

  // Shimmer effect animation
  const shimmerVariants = {
    animate: {
      backgroundPosition: ["200% 0", "-200% 0"],
      transition: {
        duration: 3,
        repeat: Infinity,
        repeatDelay: 8,
        ease: "linear"
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100'
          : 'bg-transparent'
      }`}
    >
      <nav className="container max-w-7xl mx-auto px-4 lg:px-6 md:px-4 sm:px-3" aria-label="Main navigation">
        <div className="flex items-center justify-between h-16 lg:h-20">

          {/* Logo/Brand with Creative Animations */}
          <div className="flex items-center relative">
            <motion.div
              variants={floatingVariants}
              animate="animate"
              className="relative"
            >
              <motion.div
                variants={pulseVariants}
                animate="animate"
                className="relative"
              >
                <motion.button
                  onClick={handleLogoClick}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleLogoClick(e);
                    }
                  }}
                  className="flex items-center space-x-2 group relative overflow-hidden cursor-pointer"
                  aria-label="Vasta - Home"
                  variants={logoVariants}
                  initial="hidden"
                  animate="visible"
                  whileHover="hover"
                  whileTap="tap"
                  style={{
                    background: `linear-gradient(
                      90deg,
                      transparent 0%,
                      rgba(255, 255, 255, 0.4) 50%,
                      transparent 100%
                    )`,
                    backgroundSize: "200% 100%"
                  }}
                >
                  {/* Shimmer overlay */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none"
                    variants={shimmerVariants}
                    animate="animate"
                    style={{
                      background: `linear-gradient(
                        90deg,
                        transparent 0%,
                        rgba(255, 255, 255, 0.6) 50%,
                        transparent 100%
                      )`,
                      backgroundSize: "200% 100%"
                    }}
                  />

                  {/* Glow effect background */}
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{
                      background: "radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)",
                      filter: "blur(10px)"
                    }}
                  />

                  <motion.img
                    src="https://cdn.acsdev.in/Vasta/logo_vasta.png"
                    alt="Vasta Logo"
                    className={`h-12 w-40 lg:h-16 w-auto object-contain relative z-10 pointer-events-none ${isScrolled ? 'bg-none' : 'bg-white'}`}
                    whileHover={{
                      scale: 1.02,
                      filter: "brightness(1.1) contrast(1.1)"
                    }}
                    transition={{ duration: 0.3 }}
                    style={{
                      filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))"
                    }}
                  />

                  {/* Sparkle effects */}
                  <motion.div
                    className="absolute top-2 right-2 w-2 h-2 bg-yellow-400 rounded-full opacity-0 pointer-events-none"
                    animate={{
                      opacity: [0, 1, 0],
                      scale: [0, 1, 0],
                      rotate: [0, 180, 360]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatDelay: 6,
                      delay: 1
                    }}
                  />
                  <motion.div
                    className="absolute bottom-3 left-3 w-1.5 h-1.5 bg-blue-400 rounded-full opacity-0 pointer-events-none"
                    animate={{
                      opacity: [0, 1, 0],
                      scale: [0, 1, 0],
                      rotate: [0, -180, -360]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatDelay: 6,
                      delay: 2
                    }}
                  />
                  <motion.div
                    className="absolute top-1/2 left-1 w-1 h-1 bg-green-400 rounded-full opacity-0 pointer-events-none"
                    animate={{
                      opacity: [0, 1, 0],
                      scale: [0, 1, 0],
                      x: [0, 5, 0]
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      repeatDelay: 7,
                      delay: 3
                    }}
                  />
                </motion.button>
              </motion.div>
            </motion.div>

            {/* Attention ring that appears periodically */}
            <motion.div
              className="absolute inset-0 border-2 border-blue-400 opacity-0 pointer-events-none"
              animate={{
                opacity: [0, 0.7, 0],
                scale: [0.8, 1.2, 1.4],
                borderWidth: ["2px", "1px", "0px"]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatDelay: 10,
                ease: "easeOut"
              }}
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1 lg:space-x-2">
            {navigationItems.map((item) => (
              <div key={item.name} className="relative" ref={item.isDropdown ? servicesRef : null}>
                {item.isDropdown ? (
                  // Services Dropdown
                  <div className="relative">
                    <button
                      onClick={() => setIsServicesOpen(!isServicesOpen)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setIsServicesOpen(!isServicesOpen);
                        }
                      }}
                      className={`flex items-center px-3 lg:px-4 py-2 rounded-lg text-sm lg:text-base font-bold transition-all duration-200 hover:scale-105 ${
                        isServicesActive()
                          ? isScrolled
                            ? 'text-indigo-600 bg-indigo-50 border-b-2 border-indigo-600'
                            : 'text-white bg-white/20 border-b-2 border-white'
                          : isScrolled
                          ? 'text-slate-700 hover:text-indigo-600 hover:bg-indigo-50'
                          : 'text-white/90 hover:text-white hover:bg-white/10'
                      }`}
                      aria-expanded={isServicesOpen}
                      aria-haspopup="true"
                    >
                      {item.name}
                      <svg
                        className={`ml-1 w-4 h-4 transition-transform duration-200 ${
                          isServicesOpen ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    <div
                      className={`absolute top-full left-0 mt-2 w-48 bg-white/95 backdrop-blur-md border border-gray-200 rounded-lg shadow-lg transition-all duration-200 ${
                        isServicesOpen
                          ? 'opacity-100 visible transform translate-y-0'
                          : 'opacity-0 invisible transform -translate-y-2'
                      }`}
                      role="menu"
                      aria-orientation="vertical"
                    >
                      {servicesItems.map((service) => (
                        <button
                          key={service.name}
                          onClick={() => handleServicesClick(service.name, service.path)}
                          className={`block w-full text-left px-4 py-3 transition-colors duration-200 text-sm font-semibold first:rounded-t-lg last:rounded-b-lg ${
                            isActiveRoute(service.path)
                              ? 'text-indigo-600 bg-indigo-100 border-l-4 border-indigo-600'
                              : 'text-slate-700 hover:text-indigo-600 hover:bg-indigo-50'
                          }`}
                          role="menuitem"
                        >
                          {service.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  // Regular Navigation Item
                  <button
                    onClick={() => handleNavClick(item.name, item.hasPath ? item.href : null)}
                    className={`px-3 lg:px-4 py-2 rounded-lg text-sm lg:text-lg font-bold transition-all duration-200 hover:scale-105 ${
                      isActiveRoute(item.href)
                        ? isScrolled
                          ? 'text-indigo-600 bg-indigo-50 border-b-2 border-indigo-600'
                          : 'text-white bg-white/20 border-b-2 border-white'
                        : isScrolled
                        ? 'text-slate-700 hover:text-indigo-600 hover:bg-indigo-50'
                        : 'text-white/90 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {item.name}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            <motion.button
              onClick={() => handleNavClick('Contact Us', '/contact')}
              className={`px-4 lg:px-6 py-2 lg:py-2.5 bg-gradient-to-r from-orange-600 to-yellow-600 text-white text-sm lg:text-base font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 border border-transparent hover:border-indigo-300 ${
                isActiveRoute('/contact') ? 'ring-2 ring-white ring-opacity-50' : ''
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              Contact Us
            </motion.button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <motion.button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-2 rounded-lg transition-colors duration-200 ${
                isScrolled
                  ? 'text-slate-700 hover:bg-slate-100'
                  : 'text-white hover:bg-white/10'
              }`}
              aria-label="Toggle mobile menu"
              aria-expanded={isMenuOpen}
              whileTap={{ scale: 0.95 }}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                {isMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <motion.div
          className={`md:hidden transition-all duration-300 ease-in-out ${
            isMenuOpen
              ? 'max-h-96 opacity-100 visible'
              : 'max-h-0 opacity-0 invisible'
          } overflow-hidden`}
          initial={false}
          animate={isMenuOpen ? { height: 'auto' } : { height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="py-4 space-y-2 bg-white/95 backdrop-blur-md rounded-lg mt-2 shadow-lg border border-gray-100">
            {navigationItems.map((item) => (
              <div key={item.name}>
                {item.isDropdown ? (
                  // Mobile Services Section
                  <div className="px-4">
                    <div className={`font-semibold text-base py-3 border-b border-gray-200 ${
                      isServicesActive() ? 'text-indigo-600' : 'text-slate-700'
                    }`}>
                      Other Services
                    </div>
                    {servicesItems.map((service) => (
                      <button
                        key={service.name}
                        onClick={() => handleServicesClick(service.name, service.path)}
                        className={`block w-full text-left px-4 py-2 transition-colors duration-200 text-sm ${
                          isActiveRoute(service.path)
                            ? 'text-indigo-600 bg-indigo-100 border-l-4 border-indigo-600 font-medium'
                            : 'text-slate-600 hover:text-indigo-600 hover:bg-indigo-50'
                        }`}
                      >
                        {service.name}
                      </button>
                    ))}
                  </div>
                ) : (
                  <button
                    onClick={() => handleNavClick(item.name, item.hasPath ? item.href : null)}
                    className={`block w-full text-left px-4 py-3 transition-colors duration-200 text-base font-semibold ${
                      isActiveRoute(item.href)
                        ? 'text-indigo-600 bg-indigo-100 border-l-4 border-indigo-600'
                        : 'text-slate-700 hover:text-indigo-600 hover:bg-indigo-50'
                    }`}
                  >
                    {item.name}
                  </button>
                )}
              </div>
            ))}
            <div className="px-4 pt-2">
              <motion.button
                onClick={() => handleNavClick('Contact Us', '/contact')}
                className={`w-full px-4 py-3 bg-gradient-to-r from-orange-600 to-yellow-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 ${
                  isActiveRoute('/contact') ? 'ring-2 ring-indigo-300' : ''
                }`}
                whileTap={{ scale: 0.98 }}
              >
                Contact Us
              </motion.button>
            </div>
          </div>
        </motion.div>
      </nav>
    </header>
  );
};

export default Header;
